import React, { useMemo, useState } from "react";
import { Check } from "lucide-react";
import Header from "../components/site/Header";
import Seo from "../components/site/Seo";
import Footer from "../components/site/Footer";
import CTAButton from "../components/site/CTAButton";
import Photo from "../components/site/Photo";
import {
  FAQSection, FinalCTA, Breadcrumb, SisterServices,
} from "../components/site/ServiceSections";

const CHANNELS = [
  { id: "menu", label: "Full menu library", desc: "Every dish on your menu, in your house style.", per: 1.0, baseCost: 22 },
  { id: "ubereats", label: "Uber Eats / DoorDash", desc: "Square-crop hero items and add-ons.", per: 1.0, baseCost: 18 },
  { id: "gbp", label: "Google Business Profile", desc: "Cover, interior, exterior, 10 hero dishes.", per: 0.15, baseCost: 35 },
  { id: "website", label: "Website hero & gallery", desc: "Wide editorial hero plates and atmosphere.", per: 0.12, baseCost: 45 },
  { id: "instagram", label: "Instagram / social", desc: "Vertical crops + supporting frames per dish.", per: 0.4, baseCost: 18 },
  { id: "reels", label: "Reels & short-form video", desc: "Vertical 9:16 clips, music-cleared.", per: 0.1, baseCost: 95 },
  { id: "print", label: "Print menu / posters", desc: "High-res files retouched for print.", per: 0.2, baseCost: 35 },
  { id: "campaigns", label: "Paid ads & OOH", desc: "Hero campaign frames + multi-crop set.", per: 0.08, baseCost: 65 },
];

const SIZES = [
  { val: 12, label: "Small menu — under 15 dishes" },
  { val: 22, label: "Mid menu — 15 to 30 dishes" },
  { val: 45, label: "Large menu — 31 to 60 dishes" },
  { val: 80, label: "Full library — 61 to 100 dishes" },
  { val: 120, label: "Multi-channel — 100+ dishes" },
];

export default function PhotoNeedsCalculator() {
  const [size, setSize] = useState(22);
  const [picked, setPicked] = useState({ menu: true, ubereats: true, gbp: false, website: true, instagram: true, reels: false, print: false, campaigns: false });

  const result = useMemo(() => {
    const breakdown = CHANNELS.filter(c => picked[c.id]).map(c => {
      const images = Math.max(1, Math.round(size * c.per));
      const cost = images * c.baseCost;
      return { ...c, images, cost };
    });
    const totalImages = breakdown.reduce((a, b) => a + b.images, 0);
    const totalCost = breakdown.reduce((a, b) => a + b.cost, 0);
    // Apply volume discount at 100+ images
    let adjustedCost = totalCost;
    if (totalImages >= 100) adjustedCost = Math.round(totalCost * 0.65);
    else if (totalImages >= 60) adjustedCost = Math.round(totalCost * 0.8);
    return { breakdown, totalImages, totalCost: adjustedCost, originalCost: totalCost };
  }, [size, picked]);

  const toggle = (id) => setPicked(s => ({ ...s, [id]: !s[id] }));

  return (
    <div className="min-h-screen bg-ink text-bone">
      <Seo />
      <Header />

      <section className="relative pt-32 lg:pt-40 pb-12 lg:pb-16 overflow-hidden bg-ink">
        <div className="absolute inset-0 opacity-30"><Photo slug="hero-spread-01" alt="" ratio="auto" className="!relative h-full w-full" /></div>
        <div className="absolute inset-0 bg-gradient-to-b from-ink/85 via-ink/55 to-ink" />
        <div className="relative mx-auto max-w-[1100px] px-6 lg:px-10">
          <Breadcrumb current="Photo Needs Calculator" />
          <p className="text-xs sm:text-sm uppercase tracking-wider2 text-ember mb-5 font-bold">Photo Needs Calculator · 60 seconds</p>
          <h1 className="font-display tracking-editorial leading-[0.92]" style={{ fontSize: "clamp(2.5rem, 6vw, 5rem)" }}>
            How many photos
            <br />
            do you <span className="italic font-light text-ember">actually need?</span>
          </h1>
          <p className="mt-6 max-w-2xl text-base md:text-lg leading-relaxed text-bone/80">
            Pick your menu size and the channels you actually use. We&apos;ll
            calculate how many photos you need and the indicative shoot cost.
          </p>
        </div>
      </section>

      {/* Above-tool context · 130 words */}
      <section className="bg-ink text-bone pt-4 pb-16 lg:pb-20">
        <div className="mx-auto max-w-[1100px] px-6 lg:px-10">
          <div className="grid grid-cols-12 gap-8 lg:gap-12">
            <div className="col-span-12 lg:col-span-7">
              <p className="text-xs uppercase tracking-wider2 text-ember font-bold mb-4">Why most Melbourne restaurants overpay</p>
              <p className="text-base md:text-lg text-bone/80 leading-relaxed">
                A 24-dish Vietnamese restaurant in Footscray doesn&apos;t need
                the same image library as a 12-course degustation in Fitzroy.
                Most photographers quote a flat number — we&apos;ve seen
                briefs as low as 18 images carry a venue through 12 months,
                and others need 140+ images across menu, Uber Eats, Google
                Business and Reels. This calculator uses the same ratios we
                apply internally — full menu at 1:1, Google Business at ~15%
                of dish count (cover + 10 hero items), Uber Eats matched to
                your live menu, Instagram supporting crops at ~40%. Pick the
                channels you actually run and ignore the rest.
              </p>
            </div>
            <div className="col-span-12 lg:col-span-5">
              <div className="border border-bone/15 p-5">
                <p className="text-xs uppercase tracking-wider2 text-ember font-bold mb-3">Typical Melbourne briefs</p>
                <ul className="space-y-3 text-sm text-bone/80">
                  <li><strong className="text-bone">Small café, Brunswick</strong> — 14 dishes, Instagram + Google + website = ~22 images, $480</li>
                  <li><strong className="text-bone">Mid restaurant, Carlton</strong> — 26 dishes, full menu + Uber Eats + Instagram = ~58 images, $980</li>
                  <li><strong className="text-bone">Group venue, CBD</strong> — 60 dishes across 2 menus + reels + GBP = ~110 images, $1,950</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Calculator */}
      <section className="bg-bone text-ink py-20 lg:py-28">
        <div className="mx-auto max-w-[1200px] px-6 lg:px-10">
          <div className="grid grid-cols-12 gap-10 lg:gap-16">
            {/* Inputs */}
            <div className="col-span-12 lg:col-span-7">
              <p className="text-xs uppercase tracking-wider2 text-ember font-bold mb-3">[ 01 ] Menu size</p>
              <div className="flex flex-wrap gap-2 mb-12">
                {SIZES.map(s => (
                  <button
                    key={s.val}
                    type="button"
                    data-testid={`calc-size-${s.val}`}
                    onClick={() => setSize(s.val)}
                    className={`px-4 py-2 text-[11px] uppercase tracking-wider2 font-bold border transition-all ${
                      size === s.val ? "border-ember bg-ember text-bone" : "border-ink/15 text-char hover:border-ink"
                    }`}
                  >
                    {s.label}
                  </button>
                ))}
              </div>

              <p className="text-xs uppercase tracking-wider2 text-ember font-bold mb-3">[ 02 ] Channels you actually use</p>
              <div className="grid sm:grid-cols-2 gap-3">
                {CHANNELS.map(c => (
                  <button
                    key={c.id}
                    type="button"
                    data-testid={`calc-channel-${c.id}`}
                    onClick={() => toggle(c.id)}
                    className={`text-left p-4 border transition-all ${
                      picked[c.id] ? "border-ember bg-ember/5" : "border-ink/15 hover:border-ink/40"
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <span className={`mt-1 w-4 h-4 border flex items-center justify-center shrink-0 ${
                        picked[c.id] ? "bg-ember border-ember" : "border-ink/30"
                      }`}>
                        {picked[c.id] && <Check size={10} strokeWidth={3} className="text-bone" />}
                      </span>
                      <div>
                        <p className="font-display text-base tracking-editorial leading-tight">{c.label}</p>
                        <p className="text-xs text-char-mute mt-1">{c.desc}</p>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Result */}
            <div className="col-span-12 lg:col-span-5">
              <div className="sticky top-32 bg-ink text-bone p-8 lg:p-10" data-testid="calc-result">
                <p className="text-xs uppercase tracking-wider2 text-ember font-bold mb-3">Your calculation</p>
                <p className="font-sans font-bold tracking-tight leading-none mb-3" style={{ fontSize: "clamp(3rem, 6vw, 5rem)" }}>
                  {result.totalImages}<span className="text-bone/45 text-2xl"> images</span>
                </p>
                <p className="font-display text-2xl tracking-editorial leading-tight">
                  ~${result.totalCost.toLocaleString()}<span className="text-bone/45 text-sm font-sans"> AUD ex-GST</span>
                </p>
                {result.totalCost !== result.originalCost && (
                  <p className="mt-2 text-xs text-ember">Volume discount applied · Saved ${(result.originalCost - result.totalCost).toLocaleString()}</p>
                )}

                <div className="mt-8 pt-6 border-t border-bone/15 space-y-3">
                  {result.breakdown.length === 0 ? (
                    <p className="text-sm text-bone/50 italic">Pick a channel to see the breakdown.</p>
                  ) : result.breakdown.map(b => (
                    <div key={b.id} className="flex items-baseline justify-between gap-3 text-sm">
                      <span className="text-bone/80">{b.label}</span>
                      <span className="font-sans font-bold tracking-tight">{b.images} <span className="text-bone/40 text-xs">img</span></span>
                    </div>
                  ))}
                </div>

                <a href="/get-a-quote" className="block mt-8">
                  <CTAButton variant="primary" size="md" className="w-full">Lock This Scope</CTAButton>
                </a>
                <p className="mt-4 text-xs text-bone/45 leading-relaxed">
                  Indicative only. Final quote confirmed by phone — usually inside five minutes.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <SisterServices keys={["menu", "food", "venue", "brand"]} />

      {/* Below-tool context · 135 words */}
      <section className="bg-bone text-ink py-20 lg:py-28">
        <div className="mx-auto max-w-[1100px] px-6 lg:px-10">
          <div className="grid grid-cols-12 gap-8 lg:gap-12">
            <div className="col-span-12 lg:col-span-6">
              <p className="text-xs uppercase tracking-wider2 text-ember font-bold mb-4">How to read your number</p>
              <p className="text-base md:text-lg text-char leading-relaxed">
                If the calculator put you under 30 images, you almost
                certainly only need a single 3-hour shoot — most CBD,
                Carlton or South Yarra venues at this scale book Tuesday to
                Thursday morning slots. Between 30–70 images is a standard
                half-day shoot covering web, Google Business and one
                delivery app — typical for a 22–35 dish menu in Brunswick,
                Richmond or Footscray. Above 100 images you&apos;re into a
                full-day shoot — common for two-trade venues, multi-venue
                groups, or anyone running Uber Eats, DoorDash and Menulog
                concurrently. The volume discount drops the per-image rate
                below $20 once you cross 100 images.
              </p>
            </div>
            <div className="col-span-12 lg:col-span-6">
              <p className="text-xs uppercase tracking-wider2 text-ember font-bold mb-4">What this number doesn&apos;t include</p>
              <p className="text-base md:text-lg text-char leading-relaxed">
                The estimate covers stills only. Reels are a separate
                line — typically $180 for a pack of 3 vertical clips, $350
                for 8 clips, $620 for a 15-clip campaign set. Drone
                exterior (relevant for rooftop venues in Southbank or
                Brunswick warehouse conversions) adds $220 for a CASA-
                approved sub-25kg sortie. Branded campaign concepts (talent,
                hair/makeup, art direction beyond standard hospitality
                editorial) are quoted bespoke. Everything else — surface
                styling, glassware, props, lighting, colour grading and
                first re-edit per dish — is already inside the calculator
                figure.
              </p>
            </div>
          </div>
        </div>
      </section>

      <FAQSection
        eyebrow="Common questions · Photo needs calculator"
        headline="Channels, ratios,"
        italic="and what it covers."
        intro="The calculator is opinionated based on real client briefs — it errs on the lean side, not the bloated side."
        faqs={FAQS}
      />
      <FinalCTA
        eyebrow="Final step · Hand-confirmed quotes"
        headline="Want this"
        italic="locked in writing?"
        intro="Pass your channel mix to us via the quote form — we'll respond by phone within one business day."
        backdropSlug="hero-banquet-01"
      />
      <Footer />
    </div>
  );
}

export const FAQS = [
  { q: "Does this calculator account for Uber Eats versus Google Business image rules?", a: "Yes. Uber Eats uses 1:1 square crops at minimum 968×968px — the calculator matches your live menu 1:1 to dish count. Google Business needs a cover (1080×608), 10 hero dish frames and interior/exterior — that's why it shows ~15% of menu size. Instagram supporting crops (vertical 4:5 + 9:16 Stories) sit at ~40% because most dishes need 2–3 supporting frames across the feed and Stories." },
  { q: "Why does a 28-dish Carlton restaurant return ~58 images and not 28?", a: "Because almost no Melbourne restaurant uses a single image per dish across every channel. A 28-dish menu typically needs: 28 web/menu library shots (1:1), 28 Uber Eats square crops (recropped from same shoot), 4 Google Business hero images (cover, interior, exterior, signature dish), 11 Instagram supporting frames. That's 58 images from a single half-day shoot — and the volume discount kicks in around the 60-image mark." },
  { q: "Is the cost estimate accurate for outer-metro suburbs like Werribee, Cranbourne or Sunbury?", a: "Yes — no travel fees inside Greater Melbourne (we cover all 31 LGAs from Wyndham to Casey to Hume). The estimate is within ~10% for almost every brief. Variance usually comes from urgent <72-hour turnaround, unusual access (rooftop venues, multi-floor fit-outs), or shoots that need 2+ photographers. The written quote will match what you saw within 5%." },
];
